import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { useI18n } from '@/hooks/useI18n';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import { formatPlanAmount, formatPlanDate } from '@/lib/builder/utils/payment-format';
import {
    DEFAULT_PAGE_SIZE,
    IPaidInstallment,
    IPaymentsPage,
    PeopleDictionary,
    personOf,
} from '@/lib/payments/models/payments-dashboard.model';
import { paymentMethodKey } from '@/lib/payments/utils/payment-method';
import { installmentCaption } from '@/lib/payments/utils/installment-caption';
import { PersonCell } from '@/lib/payments/components/PersonCell';
import { PlatformFeeDueBadge } from '@/lib/payments/components/PlatformFeeDueBadge';

interface RecentPaymentsCardProps {
    rows: IPaidInstallment[];
    /** Click sul titolo del viaggio: apre la modale di dettaglio, non il builder. */
    onOpenItinerary: (itineraryId: string) => void;
    /** Anagrafiche citate dalle righe: le righe portano solo la chiave. */
    people: PeopleDictionary;
    /** Righe scheletro al posto delle righe mentre la risposta arriva. */
    isLoading: boolean;
    /** Cambia a ogni risposta: le righe rientrano animate anche con gli stessi id. */
    animationKey: string | number;
    /** Admin e agenzia: la colonna del collaboratore, come nelle rate in scadenza. */
    showDesigner: boolean;
    /** Il viaggiatore non ha la colonna cliente: sarebbe il suo nome su ogni riga. */
    travelerView?: boolean;
    onOpenClient?: (clientKey: string) => void;
    onOpenAdvisor?: (advisorId: string) => void;
    /** Impaginazione della RPC, come nelle rate in scadenza: la card non sa quante pagine esistono. */
    page?: IPaymentsPage | null;
    onPageChange?: (page: number) => void;
}

/**
 * Ultime rate incassate, con la stessa tabella delle rate in scadenza: stesse colonne,
 * stesse celle persona, stessa didascalia della rata. Cambiano la data (dell'incasso) e
 * l'assenza di azioni.
 *
 * Le righe arrivano da `itinerary_installments` e non da `payments`: quella tabella e'
 * leggibile solo da service_role, e comunque contiene un record per tentativo, quindi
 * mostrerebbe anche i pagamenti falliti come se fossero incassi.
 */
export const RecentPaymentsCard = ({
    rows,
    people,
    isLoading,
    animationKey,
    showDesigner,
    travelerView,
    onOpenClient,
    onOpenAdvisor,
    page,
    onPageChange,
    onOpenItinerary,
}: RecentPaymentsCardProps) => {
    const { t } = useI18n();
    const money = useMoneyCopy();

    const columns: DataTableColumn<IPaidInstallment>[] = [
        {
            id: 'trip',
            header: t('payments.columns.trip'),
            cellClassName: 'align-top',
            cell: (row) => (
                <>
                    <button
                        type="button"
                        className="font-medium text-gray-900 hover:underline text-left"
                        onClick={() => onOpenItinerary(row.itinerary_id)}
                    >
                        {row.itinerary_title}
                    </button>
                    <span className="block text-xs text-gray-500">{installmentCaption(t, row)}</span>
                </>
            ),
        },
        ...(!travelerView
            ? [
                  {
                      id: 'client',
                      header: t('payments.columns.client'),
                      headClassName: 'whitespace-nowrap',
                      cellClassName: 'align-top',
                      cell: (row: IPaidInstallment) => (
                          <PersonCell
                              person={personOf(people, row.client_key)}
                              hint={t('payments.columns.client')}
                              withEmail
                              onOpen={
                                  row.client_key && onOpenClient
                                      ? () => onOpenClient(row.client_key as string)
                                      : undefined
                              }
                          />
                      ),
                  },
              ]
            : []),
        ...(showDesigner
            ? [
                  {
                      id: 'designer',
                      header: t('payments.columns.designer'),
                      headClassName: 'whitespace-nowrap',
                      cellClassName: 'align-top',
                      cell: (row: IPaidInstallment) => (
                          <PersonCell
                              person={personOf(people, row.designer_id)}
                              hint={t('payments.columns.designer')}
                              withEmail
                              onOpen={
                                  row.designer_id && onOpenAdvisor
                                      ? () => onOpenAdvisor(row.designer_id as string)
                                      : undefined
                              }
                          />
                      ),
                  },
              ]
            : []),
        {
            id: 'paidAt',
            header: t('payments.columns.paidAt'),
            headClassName: 'w-px whitespace-nowrap',
            cellClassName: 'align-top whitespace-nowrap',
            cell: (row) => {
                const methodKey = paymentMethodKey(row.payment_method);
                return (
                    <>
                        <span className="text-gray-900">{formatPlanDate(row.paid_at)}</span>
                        {methodKey && <span className="block text-xs text-gray-500">{t(methodKey)}</span>}
                    </>
                );
            },
        },
        {
            id: 'amount',
            header: t('payments.columns.amount'),
            headClassName: 'w-px whitespace-nowrap text-right',
            cellClassName: 'align-top text-right',
            cell: (row) => (
                <div className="flex flex-col items-end gap-1">
                    <span className="font-semibold text-green-700 tabular-nums whitespace-nowrap">
                        {formatPlanAmount(row.amount)}
                    </span>
                    {/* Sotto l'importo: e' la parte di quella cifra che non ci e' entrata. */}
                    <PlatformFeeDueBadge amount={row.platform_fee_due} />
                </div>
            ),
        },
    ];

    // Stessa tabella e stessa paginazione lato server delle altre schede.
    return (
        <DataTable
            data={rows}
            columns={columns}
            getRowId={(row) => row.installment_id}
            pageSize={page?.page_size ?? DEFAULT_PAGE_SIZE}
            serverPagination={
                onPageChange
                    ? {
                          page: page?.page ?? 1,
                          totalItems: page?.total_count ?? 0,
                          onPageChange,
                          disabled: isLoading,
                      }
                    : undefined
            }
            paginated={false}
            isLoading={isLoading}
            animationKey={animationKey}
            emptyState={t(`${money}.recentEmpty`)}
            tableClassName="table-auto min-w-[640px]"
        />
    );
};
